import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { logger } from "../../../../../shared/core/logging/logger";
import { accountStatementService } from "../../../../../shared/core/service/services";
import type { Account } from "../../../../../shared/domains/account/account";
import type { AccountStatement } from "../../../../../shared/domains/account-statements/account-statement";
import type { FeesStatement } from "../../../../../shared/domains/fees-statements/fees-statement";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { MultiSelectInput } from "../../../common/forms/multi-select-input";
import { PageBackHeader } from "../../../common/page/page-back-header";
import { MainColorSpinner } from "../../../common/spinner";
import { StaticAssets } from "../../../styles/custom-assets";
import { shadows, theme } from "../../../styles/theme";

type Statement = AccountStatement | FeesStatement;

/** MultiSelectInput keys its options by id, so both filters carry their value there. */
interface FilterOption {
  id: string;
}

interface StatementEntry {
  statement: Statement;
  account: Account;
}

interface StatementsPageProps {
  /** The account the screen was opened from; the filters still span every account, as on mobile. */
  account: Account;
  accounts: Account[];
  variant: "normal" | "fee";
  title: string;
  subtitle: string;
  emptyLabel: string;
}

/** Shared by the account and the fees statements: the same list, fed by a different endpoint. */
export function StatementsPage(props: Readonly<StatementsPageProps>) {
  const { account, accounts, variant, title, subtitle, emptyLabel } = props;
  const { formatMessage, formatDate } = useIntl();
  const { goBack } = useHistory();

  const [entries, setEntries] = useState<StatementEntry[] | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<FilterOption[]>([]);
  const [selectedYears, setSelectedYears] = useState<FilterOption[]>([]);

  const scope = useMemo(() => (accounts.length > 0 ? accounts : [account]), [accounts, account]);

  useEffect(() => {
    const fetchStatements =
      variant === "fee" ? accountStatementService.fetchFeesStatements : accountStatementService.fetchStatements;

    Promise.all(
      scope.map(async (it) => {
        try {
          const page = await fetchStatements.call(accountStatementService, it.id);
          return (page?.items ?? []).map((statement) => ({ statement, account: it }));
        } catch (e) {
          logger.debug("StatementsPage", "Failed to fetch statements", e);
          return [];
        }
      }),
    ).then((pages) => setEntries(pages.flat()));
  }, [scope, variant]);

  const items = entries ?? [];
  // Each kind keeps its own labels, so a tenant can word them differently.
  const accountLabelKey = variant === "fee" ? "feesStatements.accountLabel" : "accountsStatements.accountLabel";

  const accountOptions = useMemo(() => [...new Set(items.map((it) => it.account.id))].map((id) => ({ id })), [items]);
  const yearOptions = useMemo(
    () =>
      [...new Set(items.map((it) => String(new Date(it.statement.fromDate).getFullYear())))]
        .sort()
        .reverse()
        .map((id) => ({ id })),
    [items],
  );

  const keptAccounts = selectedAccounts.map((it) => it.id);
  const keptYears = selectedYears.map((it) => it.id);
  // No selection means no restriction, the way the mobile filters behave.
  const visibleItems = items.filter(
    (it) =>
      (keptAccounts.length === 0 || keptAccounts.includes(it.account.id)) &&
      (keptYears.length === 0 || keptYears.includes(String(new Date(it.statement.fromDate).getFullYear()))),
  );

  return (
    <>
      <PageBackHeader title={title} subtitle={subtitle} account={account} onBack={goBack} />

      <Filters>
        <MultiSelectInput
          style={{ minWidth: "200px" }}
          innerId="statement-accounts"
          selectedValues={selectedAccounts}
          options={accountOptions}
          placeholder={formatMessage("statementsFilters.allAgencies")}
          itemRenderer={(option) => formatMessage(accountLabelKey, { id: option.id })}
          onChange={setSelectedAccounts}
        />
        <MultiSelectInput
          style={{ minWidth: "160px" }}
          innerId="statement-years"
          selectedValues={selectedYears}
          options={yearOptions}
          placeholder={formatMessage("statementsFilters.years")}
          itemRenderer={(option) => option.id}
          onChange={setSelectedYears}
        />
      </Filters>

      {entries === null ? (
        <LoaderRow>
          <MainColorSpinner size="24px" />
        </LoaderRow>
      ) : visibleItems.length === 0 ? (
        <EmptyLabel>{emptyLabel}</EmptyLabel>
      ) : (
        <List>
          {visibleItems.map(({ statement, account: owner }) => (
            <Row key={`${owner.id}-${statement.id}`}>
              <RowTexts>
                <Period>
                  {formatDate(statement.fromDate, { day: "2-digit", month: "2-digit", year: "numeric" })} -{" "}
                  {formatDate(statement.toDate, { day: "2-digit", month: "2-digit", year: "numeric" })}
                </Period>
                <AccountLabel>{formatMessage(accountLabelKey, { id: owner.id })}</AccountLabel>
              </RowTexts>
              <DownloadButton
                aria-label={formatMessage("myAccountScreen.downloadRib")}
                onClick={() => accountStatementService.downloadStatement(owner.id, statement.id, variant)}
              >
                <DownloadIcon src={StaticAssets.download} alt="" />
              </DownloadButton>
            </Row>
          ))}
        </List>
      )}
    </>
  );
}

const Filters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-bottom: 20px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  overflow: hidden;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  ${shadows.sm};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid ${theme.colors.gray[100]};

  &:first-of-type {
    border-top: 0;
  }
`;

const RowTexts = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const Period = styled.span`
  ${theme.text};
  font-size: 16px;
  line-height: 22px;
  color: ${theme.colors.blue[800]};
`;

const AccountLabel = styled.span`
  ${theme.text};
  margin-top: 2px;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.gray[800]};
`;

const DownloadButton = styled(UnstyledButton)`
  display: flex;
  flex-shrink: 0;
  padding: 4px;
`;

const DownloadIcon = styled.img`
  width: 18px;
  height: 18px;
`;

const LoaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
`;

const EmptyLabel = styled.span`
  ${theme.text};
  color: ${theme.colors.gray[800]};
`;
